/**
 * 지금 화면에서 **파일을 받을 수 있는 첨부 상자**.
 *
 * 에이전트가 파일을 붙이려면 문서 번호(`appnum`)를 알아야 하는데, 그 값은 화면마다
 * 다르게 **만들어진다** — `scope.state.data.appnum` 인 화면이 있고
 * `JA001-${perid}`·`TB_E611_PLC_${actcd}` 처럼 조합하는 화면이 열댓 개다. 밖에서
 * 알아낼 방법이 없다. 그래서 **상자가 스스로 등록한다.**
 *
 * 업로드도 상자에게 맡긴다. 상자는 임시 업로드·파일 순번·목록 갱신을 이미 다 하고
 * 있어서, 그 절차를 밖에서 다시 짜면 두 벌이 되고 한쪽만 고쳐진다.
 */
export type AttachTarget = {
  /**
   * 붙일 문서가 정해졌나.
   *
   * 상자마다 문서를 가리키는 방법이 다르다 — `AttachmentBox` 는 `appnum` 하나이고
   * `CustomAttachmentBox` 는 `window` 와 `params` 다. 밖에서는 **정해졌는지만** 알면
   * 되므로 그 판단을 상자에게 맡긴다. 신규 화면에서는 아직 비어 있다.
   */
  ready(): boolean;
  /**
   * 이 상자의 화면 요소.
   *
   * **보이는 상자만 쓴다.** 이 ERP 는 화면을 탭으로 살려 두어서, 다른 화면으로 옮겨도
   * 앞 화면의 첨부 상자가 DOM 에 그대로 남는다(실측 2026-09-01: 고장완료로 옮긴 뒤에도
   * 인사등록의 상자가 잡혔다). 그대로 두면 **앞 문서에 파일이 달린다.** 창(모달) 안의
   * 상자도 창이 닫혀 있으면 보이지 않으므로 같은 규칙으로 걸러진다.
   */
  node(): Element | null;
  accept(files: File[]): Promise<void>;
};

/**
 * 등록 순서대로 쌓는다. **한 화면에 상자가 둘일 수 있어서** 하나만 두면 나중 것이
 * 앞 것을 지우고, 그 상자가 사라질 때 남은 상자까지 함께 사라진다.
 * 마지막에 등록된 살아 있는 것을 쓴다 — 사람이 보고 있는 것이 그것이다.
 */
const boxes: AttachTarget[] = [];

export function registerAttachTarget(target: AttachTarget): () => void {
  boxes.push(target);
  return () => {
    const at = boxes.indexOf(target);
    if (at >= 0) boxes.splice(at, 1);
  };
}

/**
 * 왜 못 붙이는지 사람에게 말하려면 **몇 개가 어디서 걸렸는지** 알아야 한다.
 * 「붙일 곳이 없습니다」만으로는 문서를 안 골랐는지 창이 닫혔는지 알 수 없다.
 */
export function attachDiagnosis(): { total: number; ready: number; visible: number } {
  const ready = boxes.filter((one) => one.ready());
  return { total: boxes.length, ready: ready.length, visible: ready.filter(seen).length };
}

/** 화면에 실제로 보이는가. 숨은 탭이나 닫힌 창의 상자는 보이지 않는다. */
function seen(box: AttachTarget): boolean {
  const node = box.node();
  return Boolean(node && node.isConnected && (node as HTMLElement).offsetParent);
}

/** 붙일 곳. 첨부 상자가 없는 화면이면 없다. */
export function attachTarget(): AttachTarget | null {
  for (let i = boxes.length - 1; i >= 0; i -= 1) {
    const box = boxes[i];
    if (!box) continue;
    // 문서가 아직 안 정해진 상자는 건너뛴다 — 신규 화면에서 빈 채로 뜬다.
    // 화면에 보이지 않는 상자도 건너뛴다 — 숨은 탭이나 닫힌 창의 것이다.
    if (box.ready() && seen(box)) return box;
  }
  return null;
}
